import { useState } from "react";
import { toast } from "sonner";
import { Heart, Loader2, Truck, Warehouse, Fuel, Sparkles, Wrench, Users } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp } from "@/components/Reveal";
import { api, formatApiError } from "@/lib/api";

const AMOUNTS = [10, 25, 50, 100, 250, 500];

const FUNDS = [
  { icon: Warehouse, label: "Storage unit fees" },
  { icon: Truck, label: "Truck rentals & furniture pickups" },
  { icon: Fuel, label: "Gas & transportation" },
  { icon: Sparkles, label: "Cleaning & preparing donated items" },
  { icon: Wrench, label: "Minor repairs & delivery expenses" },
  { icon: Users, label: "Furniture representatives & program operations" },
];

export default function SupportPilot() {
  const [selected, setSelected] = useState(50);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);

  const amount = custom ? parseFloat(custom) : selected;
  const valid = amount && !isNaN(amount) && amount >= 1 && amount <= 10000;

  const donate = async () => {
    if (!valid) {
      toast.error("Please choose or enter an amount between $1 and $10,000.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        amount: Math.round(amount * 100) / 100,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err, "Could not start checkout. Please try again."));
      setLoading(false);
    }
  };

  return (
    <div data-testid="support-page">
      <PageHero overline="Support the Pilot Program" title="CAN'T DONATE FURNITURE?" italic="Help fund a fresh start." testid="support-headline">
        Monetary donations keep the trucks moving, the storage units open, and the deliveries rolling — every dollar
        goes straight into helping the next family turn an empty house into a home.
      </PageHero>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <div>
            <FadeUp>
              <h2 className="font-serif text-3xl tracking-tight text-olive">Where your donation goes</h2>
            </FadeUp>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {FUNDS.map((f, i) => (
                <FadeUp key={f.label} delay={i * 0.08}>
                  <div data-testid={`fund-${f.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-3 rounded-xl border border-linen bg-white/60 p-4">
                    <f.icon className="h-6 w-6 shrink-0 text-burgundy" />
                    <span className="text-sm font-medium text-olive">{f.label}</span>
                  </div>
                </FadeUp>
              ))}
            </div>
            <FadeUp delay={0.3}>
              <p className="mt-8 border-l-2 border-burgundy pl-5 font-serif text-lg italic leading-relaxed text-olive">
                $50 can help deliver a bed frame. $500 can help furnish an entire home.
              </p>
            </FadeUp>
          </div>

          <FadeUp delay={0.15}>
            <div className="rounded-3xl border border-linen bg-white/70 p-8 shadow-xl shadow-olive/5 sm:p-10" data-testid="donation-widget">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-burgundy">
                  <Heart className="h-6 w-6 fill-cream text-cream" />
                </div>
                <h2 className="font-serif text-2xl text-olive">Make a donation</h2>
              </div>
              <div className="mt-7 grid grid-cols-3 gap-3">
                {AMOUNTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    data-testid={`amount-${a}`}
                    onClick={() => { setSelected(a); setCustom(""); }}
                    className={`rounded-xl border py-4 font-serif text-xl transition-all duration-200 ${
                      !custom && selected === a
                        ? "border-burgundy bg-burgundy text-cream"
                        : "border-linen bg-cream text-olive hover:border-burgundy/50"
                    }`}
                  >
                    ${a}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <label className="mb-1.5 block text-sm font-semibold text-olive">Custom amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-serif text-lg text-sage">$</span>
                  <input
                    data-testid="custom-amount-input"
                    type="number"
                    min="1"
                    max="10000"
                    placeholder="Enter any amount"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    className="input-field !pl-8"
                  />
                </div>
              </div>
              <button
                data-testid="monetary-donation-stripe-checkout-button"
                onClick={donate}
                disabled={loading || !valid}
                className="btn-accent mt-7 w-full !py-4 text-base disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Heart className="h-5 w-5 fill-current" />}
                {loading ? "Preparing secure checkout…" : `Donate ${valid ? `$${amount}` : "Now"}`}
              </button>
              <p className="mt-4 text-center text-xs leading-relaxed text-sage">
                Secure checkout powered by Stripe. As a pilot program, Fresh Start Furnishings is not yet a registered
                501(c)(3) — please treat donations as community support rather than tax-deductible gifts.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
